import { useCallback, useEffect, useRef } from 'react';
import type { EditorState } from 'lexical';

const DEFAULT_DEBOUNCE_MS = 1500;

/**
 * Debounced OnChange handler for the Lexical canvas.
 *
 * v3.0 NOTE: there is no `PATCH /documents/{id}/content` endpoint, so manual
 * free-typing edits are NOT persisted server-side — AI-generated drafts persist
 * via the operation lineage instead (each op returns a draft id). This hook
 * therefore only forwards the serialized EditorState to an in-memory `onSave`
 * callback. When the BE adds a content-patch endpoint, point `onSave` at it —
 * no other change needed. (Tracked in docs/architectureDesign.md.)
 */
export function useDebouncedDocumentSave(
  onSave?: (serializedState: string) => void,
  delayMs: number = DEFAULT_DEBOUNCE_MS,
) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  return useCallback(
    (editorState: EditorState) => {
      if (!onSave) return;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        const json = JSON.stringify(editorState.toJSON());
        onSave(json);
      }, delayMs);
    },
    [onSave, delayMs],
  );
}
